import {css} from '@mui/material';
import {TooltipWrapperCore} from '@wallaby-ui/core';
import React from 'react';
import {useIntl} from 'react-intl';

import {AlertEpisodeTooltipDto} from './alert_types';
import {AlertCriteriaRenderer, AlertGenericCriteriaChips} from './AlertCriteriaChips';

type Props = {
  /** The saved-filter component the alert is defined on. */
  filterComponent: string;
  /** The current criteria as stored. */
  filterData: AlertEpisodeTooltipDto['filterData'];
  /** Product renderer of the criteria chips (the list pages' own chip builders); the generic chips otherwise. */
  renderCriteria?: AlertCriteriaRenderer;
  children: React.ReactNode;
};

/* the white card of the Alert Board design: 330 wide, the header and the criteria as grey pills */
const popperCss = css`
  width: 330px;
  max-width: 330px;
  background-color: #fff;
  color: var(--dlThemeGray-800);
  border: 1px solid var(--dlThemeGray-100);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  padding: 14px 16px;
  line-height: normal;
  text-align: left;
`;
const wrapperCss = css`
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;
  cursor: help;
`;
const headerCss = css`
  display: block;
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dlTheme-600);
  margin-bottom: 10px;
`;

/**
 * The definition tooltip of the alert board (PDW-184): what the alert matches on, nothing else. Header "Alert
 * definition" and the current criteria as grey chips; no version, no author, no footer and no variant for a
 * retired alert, whose row already shows it. The content mounts only while the tooltip is open, so a renderer may
 * load its code lists lazily.
 */
export const AlertDefinitionTooltip: React.FC<Props> = (props) => {
  const {filterComponent, filterData, renderCriteria, children} = props;
  const intl = useIntl();
  const content = (
    <div data-name={'dl-alert-definition-tooltip'}>
      <span css={headerCss}>{intl.formatMessage({id: 'alert.episode.tooltip.title'})}</span>
      {renderCriteria ? (
        renderCriteria(filterComponent, filterData, {variant: 'board'})
      ) : (
        <AlertGenericCriteriaChips filterComponent={filterComponent} filterData={filterData} variant='board' />
      )}
    </div>
  );
  return (
    <TooltipWrapperCore tooltip={content} noPadding wrapperCss={wrapperCss} popperInputCss={popperCss} placement='bottom-start'>
      {children}
    </TooltipWrapperCore>
  );
};

AlertDefinitionTooltip.displayName = 'AlertDefinitionTooltip';
